import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Cloud, RefreshCw, CheckCircle2, AlertCircle, Loader, BarChart3 } from 'lucide-react';

/**
 * HIRA 비급여 진료비 동기화 관리자 페이지
 * - 수동 트리거 (전체 시도)
 * - 분기 자동 동기화 상태 모니터
 * - 시도·시술별 평균 가격 통계 미리보기
 */
function AdminHiraNonpaymentSync() {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({ count: 0, regions: 0, treatments: 0 });
    const [recentLogs, setRecentLogs] = useState([]);

    useEffect(() => { loadStats(); loadLogs(); }, []);

    const loadStats = async () => {
        try {
            const snap = await getDocs(collection(db, 'hiraAveragePrices'));
            const regions = new Set();
            const treatments = new Set();
            snap.docs.forEach(d => {
                const data = d.data();
                if (data.region) regions.add(data.region);
                if (data.treatment) treatments.add(data.treatment);
            });
            setStats({ count: snap.size, regions: regions.size, treatments: treatments.size });
        } catch (_) { /* skip */ }
    };

    const loadLogs = async () => {
        try {
            const snap = await getDocs(query(
                collection(db, 'syncLogs'),
                orderBy('finishedAt', 'desc'),
                limit(10),
            ));
            setRecentLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (_) { /* skip */ }
    };

    const handleSync = async () => {
        if (!window.confirm('전국 17개 시도의 치과 비급여 데이터를 동기화합니다.\n약 5~10분 소요될 수 있습니다. 시작할까요?')) return;
        setRunning(true);
        setError(null);
        setResult(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const fn = httpsCallable(fns, 'adminSyncHiraNonpayment');
            const res = await fn({});
            setResult(res.data);
            await loadStats();
            await loadLogs();
        } catch (e) {
            setError(e.message || '동기화 실패');
        }
        setRunning(false);
    };

    return (
        <div style={S.page}>
            <div style={S.header}>
                <h1 style={S.title}>
                    <Cloud size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    HIRA 비급여 진료비 동기화
                </h1>
                <p style={S.subtitle}>
                    건강보험심사평가원 OpenAPI 에서 전국 치과의원·치과병원 비급여 가격 데이터를 가져와
                    광역시·도 단위 평균을 환자앱에 노출합니다.
                </p>
            </div>

            <div style={S.statsRow}>
                <div style={S.statCard}>
                    <BarChart3 size={18} color="#0ea5e9" />
                    <div style={S.statNum}>{stats.count}</div>
                    <div style={S.statLabel}>총 데이터</div>
                </div>
                <div style={S.statCard}>
                    <BarChart3 size={18} color="#10b981" />
                    <div style={S.statNum}>{stats.regions}</div>
                    <div style={S.statLabel}>광역 시·도</div>
                </div>
                <div style={S.statCard}>
                    <BarChart3 size={18} color="#f59e0b" />
                    <div style={S.statNum}>{stats.treatments}</div>
                    <div style={S.statLabel}>시술 종류</div>
                </div>
            </div>

            <div style={S.section}>
                <h3 style={S.sectionTitle}>수동 동기화</h3>
                <p style={S.sectionDesc}>
                    분기 자동 (매분기 첫째 일 03:00 KST) 외에 즉시 갱신이 필요할 때 사용합니다.
                </p>
                <button onClick={handleSync} disabled={running} style={S.syncBtn}>
                    {running ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                    {running ? '동기화 중... (5~10분)' : '전국 17개 시도 동기화 시작'}
                </button>
            </div>

            {error && (
                <div style={S.error}>
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {result && (
                <div style={S.success}>
                    <CheckCircle2 size={16} />
                    <strong>완료:</strong> {result.quarter} 동기화 — {result.results.length}개 시도 처리
                </div>
            )}

            {result?.results && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>최근 결과</h3>
                    <table style={S.table}>
                        <thead>
                            <tr><th style={S.th}>시도</th><th style={S.th}>총 건수</th><th style={S.th}>시술 종류</th><th style={S.th}>상태</th></tr>
                        </thead>
                        <tbody>
                            {result.results.map((r, i) => (
                                <tr key={i} style={S.tr}>
                                    <td style={S.td}><strong>{r.sidoName}</strong></td>
                                    <td style={S.td}>{r.totalItems || 0}</td>
                                    <td style={S.td}>{r.treatments?.length || 0}</td>
                                    <td style={S.td}>
                                        {r.error
                                            ? <span style={{ color: '#ef4444' }}>실패: {r.error}</span>
                                            : <span style={{ color: '#10b981' }}>✓ 완료</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {recentLogs.length > 0 && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>이전 동기화 이력 (최근 10건)</h3>
                    <table style={S.table}>
                        <thead>
                            <tr><th style={S.th}>시점</th><th style={S.th}>분기</th><th style={S.th}>유형</th><th style={S.th}>처리</th></tr>
                        </thead>
                        <tbody>
                            {recentLogs.map(log => (
                                <tr key={log.id} style={S.tr}>
                                    <td style={S.td}>{log.finishedAt?.toDate ? new Date(log.finishedAt.toDate()).toLocaleString('ko-KR') : '-'}</td>
                                    <td style={S.td}>{log.quarter || '-'}</td>
                                    <td style={S.td}>{log.type || '-'}</td>
                                    <td style={S.td}>{log.results?.filter(r => r.ok).length || 0} / {log.results?.length || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { maxWidth: 1100, margin: '0 auto', padding: 24 },
    header: { marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 900, margin: 0, color: '#0f172a' },
    subtitle: { fontSize: 13, color: '#64748b', marginTop: 6, lineHeight: 1.7 },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
    statCard: {
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    },
    statNum: { fontSize: 24, fontWeight: 900, color: '#0f172a' },
    statLabel: { fontSize: 11, color: '#64748b' },
    section: { marginBottom: 20, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
    sectionTitle: { fontSize: 15, fontWeight: 800, marginBottom: 6, color: '#0f172a' },
    sectionDesc: { fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 },
    syncBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '10px 18px', background: '#0ea5e9', color: '#fff', border: 'none',
        borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer',
    },
    error: { padding: 12, marginBottom: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
    success: { padding: 12, marginBottom: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th: { textAlign: 'left', padding: 8, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 800, color: '#64748b', fontSize: 11 },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: 8 },
};

export default AdminHiraNonpaymentSync;
